import { useState, useEffect, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from '../ui/button'
import { Logo } from '../ui/Logo'
import { useI18n } from '../../i18n'
import { useWebsite } from '../../hooks/use-website'
import { useWebsiteEditor } from '../../hooks/use-website-editor'
import { BrandingContext } from '../../contexts/BrandingContext'
import WebsitePageList from './WebsitePageList'
import WebsiteAIPanel from './WebsiteAIPanel'
import WebsiteBlockEditor from './WebsiteBlockEditor'
import ButlerConfigPanel from './ButlerConfigPanel'
import BlockLibrary from './BlockLibrary'
import SortableBlock from './SortableBlock'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragEndEvent, DragOverlay, DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  Monitor, Tablet, Smartphone, Eye, Globe,
  Layout, MessageSquare, Bot, Copy, ExternalLink,
  Save, Layers, ArrowLeft,
} from 'lucide-react'
import type { WebsitePage, WebsiteBlock as WB, BlockType } from '../../types'
import { getPublicUrl } from '../../helper/website-url'

type SidebarTab = 'pages' | 'blocks' | 'ai' | 'butler'

export default function WebsiteEditorPage() {
  const { websiteId } = useParams<{ websiteId: string }>()
  const { t } = useI18n()
  const {
    website, setWebsite, loading, saving, fetchWebsite, publishWebsite, unpublishWebsite,
    updateBlock, deletePage, addPage, reorderPages, addBlock, deleteBlock,
    saveWebsite,
  } = useWebsite()
  const editor = useWebsiteEditor(websiteId || '')
  const [activePage, setActivePage] = useState<WebsitePage | null>(null)
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop')
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('pages')
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [addingBlock, setAddingBlock] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [draggingBlockId, setDraggingBlockId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  useEffect(() => {
    fetchWebsite(websiteId)
  }, [fetchWebsite, websiteId])

  useEffect(() => {
    if (website?.pages?.length && !activePage) {
      const home = website.pages.find(p => p.slug === 'home') || website.pages[0]
      setActivePage(home)
    }
  }, [website])

  // Warn before leaving with unsaved changes
  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); return '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  if (loading || !website) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="animate-pulse text-gray-300">{t('加载中...', 'Loading...')}</div>
      </div>
    )
  }

  const selectedBlock: WB | null = activePage?.blocks?.find(b => b.id === selectedBlockId) || null

  const handlePublish = async () => {
    if (website.status === 'PUBLISHED') {
      await unpublishWebsite()
    } else {
      const result = await publishWebsite()
      if (result) {
        window.open(result.url, '_blank')
      }
    }
  }

  const handleBlockUpdate = async (blockId: string, data: { props?: Record<string, any>; visible?: boolean }) => {
    // Optimistic update: apply change locally first
    if (website && activePage) {
      const updatedPages = website.pages.map(p => ({
        ...p,
        blocks: p.blocks.map(b =>
          b.id === blockId ? { ...b, ...data } : b
        ),
      }))
      setWebsite({ ...website, pages: updatedPages })
      // Also update activePage reference
      const updatedActivePage = updatedPages.find(p => p.id === activePage.id)
      if (updatedActivePage) setActivePage(updatedActivePage)
    }

    const ok = await updateBlock(blockId, data)
    if (!ok) {
      // Revert on failure
      await fetchWebsite(websiteId)
    }
    setDirty(true)
  }

  const handleAddBlock = async (type: BlockType) => {
    if (!activePage) return
    setAddingBlock(true)
    const ok = await addBlock({
      pageId: activePage.id,
      blockType: type,
      blockOrder: activePage.blocks.length,
    })
    if (ok) {
      await fetchWebsite(websiteId)
      setDirty(true)
    }
    setAddingBlock(false)
  }

  const handleDeleteBlock = async (blockId: string) => {
    const ok = await deleteBlock(blockId)
    if (ok) {
      if (selectedBlockId === blockId) setSelectedBlockId(null)
      await fetchWebsite(websiteId)
      setDirty(true)
    }
  }

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingBlockId(String(event.active.id))
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    setDraggingBlockId(null)
    const { active, over } = event
    if (!over || active.id === over.id || !activePage) return

    const blocks = [...activePage.blocks].sort((a, b) => a.blockOrder - b.blockOrder)
    const oldIndex = blocks.findIndex(b => b.id === active.id)
    const newIndex = blocks.findIndex(b => b.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    // Reorder locally
    const [moved] = blocks.splice(oldIndex, 1)
    blocks.splice(newIndex, 0, moved)
    const reordered = blocks.map((b, i) => ({ ...b, blockOrder: i }))

    // Optimistically update activePage
    setActivePage({ ...activePage, blocks: reordered })
    setDirty(true)

    // Save to backend
    await saveWebsite({
      pages: website.pages.map(p =>
        p.id === activePage.id ? { ...p, blocks: reordered } : p
      ),
      nav: website.nav,
      branding: website.branding,
      butler: website.butler,
    })
    await fetchWebsite(websiteId)
  }

  const handleSave = async () => {
    const ok = await saveWebsite({
      pages: website.pages,
      nav: website.nav,
      branding: website.branding,
      butler: website.butler,
    })
    if (ok) setDirty(false)
  }

  const deviceWidth = device === 'mobile' ? 'max-w-[375px]' : device === 'tablet' ? 'max-w-[768px]' : 'max-w-full'

  const sortedBlocks = activePage
    ? [...activePage.blocks].sort((a, b) => a.blockOrder - b.blockOrder)
    : []

  return (
    <BrandingContext.Provider value={{ branding: website.branding, isEditing: true }}>
      <div className="h-screen flex flex-col bg-[#fafafa]">
        {/* Header */}
        <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 shrink-0 z-30">
          <Link to="/my-website" className="flex items-center gap-2 text-lg font-semibold mr-4 shrink-0">
            <Logo size={24} />
            <span className="text-[#1a1a1a]">PrimeBook</span>
            <span className="text-orange-500">AI</span>
          </Link>
          <Link to="/my-website"
            className="px-2.5 py-1 text-[12px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('返回', 'Back')}
          </Link>

          <span className="text-sm text-gray-400 hidden sm:inline">
            {website.companyName} · {t('官网编辑器', 'Website Editor')}
          </span>

          <div className="ml-auto flex items-center gap-2">
            {/* Device toggle */}
            <div className="hidden md:flex items-center gap-1 mr-2 p-1 rounded-lg bg-black/[0.03]">
              {([['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]] as const).map(([d, Icon]) => (
                <button key={d} onClick={() => setDevice(d)}
                  className={`p-1.5 rounded-md text-xs transition-all ${device === d ? 'bg-white shadow-sm text-[#1a1a1a]' : 'text-gray-400 hover:text-[#1a1a1a]'}`}>
                  <Icon className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>

            {/* Public link with copy */}
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-100 max-w-xs">
              <Globe className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              <span className="text-[11px] text-gray-500 truncate select-all">{getPublicUrl(website.shortcode)}</span>
              <button className="shrink-0 p-0.5 rounded hover:bg-gray-200 transition-colors"
                title={t('复制链接', 'Copy link')}
                onClick={() => {
                  navigator.clipboard.writeText(getPublicUrl(website.shortcode))
                  setCopiedUrl(true)
                  setTimeout(() => setCopiedUrl(false), 2000)
                }}>
                <Copy className={`h-3 w-3 ${copiedUrl ? 'text-emerald-500' : 'text-gray-400'}`} />
              </button>
              <a href={`/e/${website.shortcode}`} target="_blank" rel="noopener noreferrer"
                className="shrink-0 p-0.5 rounded hover:bg-gray-200 transition-colors"
                title={t('打开官网', 'Open website')}>
                <ExternalLink className="h-3 w-3 text-gray-400" />
              </a>
            </div>

            <Link to={`/e/${website.shortcode}`} target="_blank"
              className="sm:hidden px-3 py-1.5 text-[13px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
              <Eye className="h-3.5 w-3.5 inline mr-1.5" />
              {t('预览', 'Preview')}
            </Link>

            {/* Save button */}
            <Button onClick={handleSave} disabled={!dirty || saving}
              className={`h-8 px-4 text-xs font-medium rounded-lg ${dirty ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-gray-100 text-gray-400 cursor-default'}`}>
              <Save className="h-3 w-3 mr-1.5" />
              {saving ? t('保存中...', 'Saving...') : t('保存', 'Save')}
            </Button>

            <Button onClick={handlePublish}
              className={`h-8 px-4 text-xs font-medium rounded-lg ${website.status === 'PUBLISHED' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}>
              <Globe className="h-3 w-3 mr-1.5" />
              {website.status === 'PUBLISHED' ? t('已上线', 'Live') : t('发布', 'Publish')}
            </Button>
          </div>
        </header>

        <div className="flex flex-1 min-h-0">
          {/* Left Sidebar */}
          <aside className="w-72 flex flex-col border-r border-black/[0.04] bg-white/50 shrink-0">
            {/* Sidebar tabs */}
            <div className="flex border-b border-black/[0.04]">
              {([
                ['pages', Layout, t('页面', 'Pages')],
                ['blocks', Layers, t('区块', 'Blocks')],
                ['ai', MessageSquare, t('AI编辑', 'AI Edit')],
                ['butler', Bot, t('管家', 'Butler')],
              ] as [SidebarTab, typeof Layout, string][]).map(([tab, Icon, label]) => (
                <button key={tab} onClick={() => setSidebarTab(tab)}
                  className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs transition-all ${sidebarTab === tab ? 'text-[#1a1a1a] font-medium border-b-2 border-orange-500' : 'text-gray-400 hover:text-[#1a1a1a]'}`}>
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">{label}</span>
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto">
              {sidebarTab === 'pages' && (
                <WebsitePageList
                  pages={website.pages}
                  activePage={activePage}
                  onPageSelect={(page) => { setActivePage(page); setSelectedBlockId(null) }}
                  onAddPage={addPage}
                  onDeletePage={deletePage}
                  onReorderPages={reorderPages}
                />
              )}
              {sidebarTab === 'blocks' && (
                <BlockLibrary onAddBlock={handleAddBlock} adding={addingBlock} />
              )}
              {sidebarTab === 'ai' && (
                <WebsiteAIPanel
                  websiteId={websiteId || ''}
                  activePageId={activePage?.id}
                  selectedBlockId={selectedBlockId}
                  messages={editor.messages}
                  isEditing={editor.isEditing}
                  streamingText={editor.streamingText}
                  onSend={editor.sendEditInstruction}
                  onUndo={editor.undoLastEdit}
                  onRefresh={() => fetchWebsite(websiteId)}
                />
              )}
              {sidebarTab === 'butler' && (
                <ButlerConfigPanel
                  butler={website.butler}
                  onUpdate={() => fetchWebsite(websiteId)}
                />
              )}
            </div>
          </aside>

          {/* Main preview with DnD */}
          <main className="flex-1 overflow-y-auto bg-gray-100/50" onClick={() => setSelectedBlockId(null)}>
            <div className={`mx-auto ${deviceWidth} transition-all duration-300 min-h-full bg-white shadow-sm`}>
              {activePage && sortedBlocks.length > 0 ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter}
                  onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                  <SortableContext items={sortedBlocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                    {sortedBlocks.map(block => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        isEditing={true}
                        isSelected={selectedBlockId === block.id}
                        onSelect={setSelectedBlockId}
                        onDelete={handleDeleteBlock}
                      />
                    ))}
                  </SortableContext>
                  <DragOverlay>
                    {draggingBlockId ? (
                      <div className="opacity-70 bg-white shadow-xl rounded-lg border border-gray-200 p-2">
                        <span className="text-xs text-gray-400">
                          {sortedBlocks.find(b => b.id === draggingBlockId)?.blockType}
                        </span>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              ) : activePage ? (
                <div className="flex flex-col items-center justify-center h-96 text-gray-300">
                  <Layers className="h-8 w-8 mb-3" />
                  <p className="text-sm">{t('当前页面暂无内容', 'No content on this page')}</p>
                  <p className="text-xs mt-1 mb-4">{t('从区块库添加内容，或使用AI编辑', 'Add blocks from the library, or use AI Edit')}</p>
                  <Button variant="outline" size="sm"
                    className="border-dashed border-gray-300 text-gray-400 hover:text-orange-500 hover:border-orange-300"
                    onClick={() => setSidebarTab('blocks')}>
                    <Layers className="h-3.5 w-3.5 mr-1.5" />
                    {t('添加区块', 'Add Block')}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center h-96 text-gray-300">
                  {t('请选择一个页面', 'Select a page')}
                </div>
              )}
            </div>
          </main>

          {/* Right sidebar: Block editor */}
          {selectedBlock && (
            <aside className="w-80 border-l border-black/[0.04] bg-white/50 overflow-y-auto shrink-0">
              <WebsiteBlockEditor
                block={selectedBlock}
                onUpdate={handleBlockUpdate}
                onClose={() => setSelectedBlockId(null)}
              />
            </aside>
          )}
        </div>
      </div>
    </BrandingContext.Provider>
  )
}
